/**
 * Generic Wizard Progress Indicator
 * Shows progress bar with dots for current position
 * Extracted from KYCProgressIndicator and generified
 */

import React from 'react';
import { useWizardContext } from './WizardContext';

const WizardProgressIndicator: React.FC = () => {
  const { config, currentStep, totalSteps, completedSteps } = useWizardContext();

  // Calculate progress percentage
  const progressPercentage = ((currentStep - 1) / (totalSteps - 1)) * 100;
  const completedPercentage = (completedSteps.size / totalSteps) * 100;

  return (
    <div className="flex items-center gap-6">
      {/* Step counter */}
      <div className="text-sm">
        <span className="font-medium">Step {currentStep}</span>
        <span className="text-gray-300"> of {totalSteps}</span>
      </div>

      {/* Progress bar */}
      <div className="relative w-48">
        <div className="text-xs text-gray-300 mb-1">
          {Math.round(completedPercentage)}% Complete
        </div>
        <div className="relative">
          {/* Background track */}
          <div className="h-2 bg-white bg-opacity-20 rounded-full overflow-hidden">
            {/* Progress fill */}
            <div
              className={`h-full ${config.theme.progressBarColor || 'bg-kielis-gold'} rounded-full transition-all duration-500 ease-out`}
              style={{ width: `${progressPercentage}%` }}
              role="progressbar"
              aria-valuenow={currentStep}
              aria-valuemin={1}
              aria-valuemax={totalSteps}
              aria-label={`Step ${currentStep} of ${totalSteps}`}
            />
          </div>

          {/* Step dots */}
          <div className="absolute inset-0 flex items-center justify-between px-1">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
              <div
                key={step}
                className={`
                  w-2 h-2 rounded-full transition-all duration-300
                  ${step <= currentStep
                    ? `${config.theme.progressDotColor || 'bg-white'} scale-110`
                    : 'bg-white bg-opacity-40 scale-75'
                  }
                  ${completedSteps.has(step)
                    ? `ring-2 ${config.theme.progressDotRing || 'ring-kielis-gold'} ring-offset-1 ring-offset-kielis-navy`
                    : ''
                  }
                `}
                title={`Step ${step}${completedSteps.has(step) ? ' (Completed)' : ''}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile-friendly condensed view */}
      <div className="sm:hidden flex items-center gap-2">
        <div className="flex items-center gap-1">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
            <div
              key={step}
              className={`
                w-1.5 h-1.5 rounded-full
                ${step === currentStep
                  ? `${config.theme.progressBarColor || 'bg-kielis-gold'} w-6`
                  : step < currentStep
                  ? 'bg-white'
                  : 'bg-white bg-opacity-40'
                }
              `}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WizardProgressIndicator;
